import type { BigNumber, BigNumberish } from '@ethersproject/bignumber';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEntryPoint } from '@ui/components/EntryPointProvider/EntryPointProvider';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import { QueryKey } from '@ui/queries/queryKey';
import bigNumberToNumber from '@ui/utils/bigNumberToNumber';
import { trackEvent } from '@ui/utils/tracking';
import type { ContractReceipt, VenoSDK } from '@veno-app/sdk';

import { QUERY_KEY_ATOM_BALANCE } from './useAtomBalance';
import { useBalance } from './useBalance';
import type { CoinMode } from './useCoinMode';
import { useCoinMode } from './useCoinMode';
import { useLiquidCoinBalance } from './useLiquidCoinBalance';

const useStakeCoin = (coinMode?: CoinMode) => {
  const { refetch: refetchCroBalance, data: balanceCro } = useBalance();
  const globalCoinMode = useCoinMode();
  const mode = coinMode ?? globalCoinMode;
  const queryClient = useQueryClient();
  const sdk = useVenoSDK();
  const stakeFn = async (receiver: string, amount: BigNumberish) => {
    const data =
      mode === 'cro'
        ? await sdk.LiquidCro.stake(receiver, amount)
        : mode === 'eth'
        ? await sdk.LiquidEth.stake(receiver, amount)
        : await sdk.LiquidAtom.stake(receiver, amount);
    const txReceipt = await data.txReceiptPromise;
    return txReceipt;
  };

  const entrypoint = useEntryPoint();

  const stake = ({
    receiver,
    amount,
  }: {
    receiver: string;
    amount: BigNumberish;
  }) => stakeFn(receiver, amount);

  return useMutation(stake, {
    onSuccess: async (data, vars) => {
      queryClient.invalidateQueries([QueryKey.TOTAL_POOLED]);
      queryClient.invalidateQueries(useLiquidCoinBalance.queryKey(mode));

      if (mode === 'cro') {
        refetchCroBalance();
        queryClient.invalidateQueries([QueryKey.TOTAL_SUPPLY_STCRO]);
        queryClient.invalidateQueries(['useBalance']);
        trackStakeEvent(sdk, data, vars, balanceCro, entrypoint);
      } else if (mode === 'atom') {
        queryClient.invalidateQueries([QUERY_KEY_ATOM_BALANCE]);
      } else if (mode === 'eth') {
        queryClient.invalidateQueries(['useBalance']);
      }
    },
  });
};

function trackStakeEvent(
  sdk: VenoSDK,
  data: ContractReceipt,
  vars: { receiver: string; amount: BigNumberish },
  balance: number | undefined,
  entrypoint: string,
) {
  const receivedLcro: BigNumber | null = sdk.LiquidCro.getReceivedLcro(data);
  const receivedLcroNumber = bigNumberToNumber(receivedLcro ?? 0);
  const amountNumber = bigNumberToNumber(vars.amount);
  const balanceRemaining = balance ? balance - amountNumber : undefined;
  trackEvent('Stake CRO Confirmed', {
    'LCRO received': receivedLcroNumber,
    isTransaction: true,
    Amount: amountNumber,
    'Remaining Balance': balanceRemaining,
    Entrypoint: entrypoint,
  });
}

export default useStakeCoin;
